/**
 * @file orderLineQuantity.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-23
 * @modified 2026-10-23
 * @description No customer order line at a quantity of 0 (Cam, 23 Oct 2026 —
 *              "Please don't allow 0 in qty when creating customer order").
 *              One rule for Create / Edit Customer Order and the order save
 *              (POST / PUT /api/orders/customer-orders):
 *
 *   - a NEW line must have a quantity above 0 (blank, 0 and below 0 are
 *     refused, the line named);
 *   - on an edit, a line already on the order whose quantity is not changed
 *     is left alone — an old order with a 0 line still saves — but a line
 *     changed to 0 (or blank, or below 0) is refused like a new one.
 *
 *   The server's utils/orderLineQuantity.js is GENERATED from this file by
 *   scripts/sync-shared-utils.js — edit this one and re-run it.
 */

const blank = (v) => v === '' || v === null || v === undefined || String(v).trim() === '';

/** What is wrong with a line's quantity: null (fine), 'blank', 'zero' or 'negative'. */
export function quantityProblem(value) {
  if (blank(value)) return 'blank';
  const n = Number(value);
  if (!Number.isFinite(n)) return 'blank';
  if (n === 0) return 'zero';
  if (n < 0) return 'negative';
  return null;
}

const WHY = {
  blank: 'has no quantity',
  zero: 'has a quantity of 0',
  negative: 'has a quantity below 0'
};

/** The words for one problem, as a cell's flag says it. */
export function quantityProblemText(problem) {
  return problem ? `Quantity ${problem === 'blank' ? 'missing' : (problem === 'zero' ? 'is 0' : 'is below 0')} — enter more than 0` : '';
}

const storedQty = (stored, id) => {
  if (!stored || id === null || id === undefined) return undefined;
  const v = typeof stored.get === 'function' ? stored.get(id) ?? stored.get(Number(id)) ?? stored.get(String(id)) : stored[id];
  return v === undefined ? undefined : (Number(v) || 0);
};

/**
 * The lines a save must refuse for their quantity.
 * @param {Array<object>} lines   the lines being saved ({ id, quantity, product_code, product_name })
 * @param {Map|object|null} stored the order's lines as stored — id → quantity
 *        (an edit); null for a new order
 * @returns {Array<{ index, id, problem, label, text }>}
 */
export function lineQuantityRefusals(lines, stored = null) {
  const out = [];
  (Array.isArray(lines) ? lines : []).forEach((line, index) => {
    if (!line) return;
    const problem = quantityProblem(line.quantity);
    if (!problem) return;
    const before = storedQty(stored, line.id);
    // An existing line whose quantity this save does not change is left alone.
    if (before !== undefined && (Number(line.quantity) || 0) === before) return;
    const name = [line.product_code, line.product_name].map(s => String(s || '').trim()).filter(Boolean).join(' ');
    const label = `Line ${index + 1}${name ? ` (${name})` : ''}`;
    out.push({ index, id: line.id ?? null, problem, label, text: `${label} ${WHY[problem]}` });
  });
  return out;
}

/** One sentence for a refusal, or '' when there is none. */
export function quantityRefusalMessage(refusals) {
  if (!refusals || !refusals.length) return '';
  const list = refusals.map(r => r.text);
  const joined = list.length === 1 ? list[0] : `${list.slice(0, -1).join('; ')} and ${list[list.length - 1]}`;
  return `${joined} — enter a quantity above 0${refusals.length > 1 ? ' on each' : ''}, or remove the line.`;
}
